import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { AUTH_STORAGE_KEYS } from '@/constants/auth';
import { getMasterPublicBaseUrl } from '@easyflow/shared';

interface ImpersonationToken {
  id: string;
  email: string;
  tenant_id: string;
  tenant_slug: string;
  role: string;
  is_impersonating: boolean;
  impersonated_by: string;
  actor_sub: string;
  actor_role: string;
  actor_is_master: boolean;
  exp: number;
}

export default function ImpersonateHandler() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processImpersonation = async () => {
      const token = searchParams.get('token');
      const backupToken = searchParams.get('backup');
      
      if (!token) {
        setError('Token di impersonazione non fornito');
        return;
      }

      try {
        const decoded = jwtDecode<ImpersonationToken>(token);
        
        if (!decoded.is_impersonating) {
          setError('Token non valido per impersonazione');
          return;
        }

        if (decoded.exp * 1000 < Date.now()) {
          setError('Token di impersonazione scaduto');
          return;
        }

        try {
          await fetch('/api/bff/logout', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', 'X-Auth-Scope': 'tenant' }
          });
        } catch (err) {
          console.error('[ImpersonateHandler] Failed to clear session:', err);
        }
        
        localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, token);
        localStorage.setItem(AUTH_STORAGE_KEYS.IMPERSONATION_TOKEN, token);
        
        if (backupToken) {
          localStorage.setItem(AUTH_STORAGE_KEYS.MASTER_BACKUP_TOKEN, backupToken);
        }
        
        const userData = {
          id: decoded.id,
          email: decoded.email,
          role: decoded.role,
          tenant_id: decoded.tenant_id,
          tenantSlug: decoded.tenant_slug,
          firstName: decoded.email.split('@')[0],
          lastName: '',
          is_impersonating: true
        };
        localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(userData));
        localStorage.setItem(AUTH_STORAGE_KEYS.TENANT_SLUG, decoded.tenant_slug);
        
        localStorage.setItem(AUTH_STORAGE_KEYS.IMPERSONATION_INFO, JSON.stringify({
          originalUser: {
            firstName: decoded.email.split('@')[0],
            lastName: '',
            email: decoded.email,
            role: decoded.role
          },
          impersonatedAt: new Date().toISOString(),
          is_impersonating: true,
          impersonated_by: decoded.impersonated_by,
          actor_sub: decoded.actor_sub,
          actor_role: decoded.actor_role,
          actor_is_master: decoded.actor_is_master
        }));

        window.location.href = `/${decoded.tenant_slug}/dashboard`;
      } catch (err) {
        console.error('Error processing impersonation token:', err);
        setError('Errore durante l\'elaborazione del token');
      }
    };
    
    processImpersonation();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="bg-red-900/50 border border-red-500 rounded-lg p-6 max-w-md">
          <h2 className="text-xl font-bold text-red-300 mb-2">Errore Impersonazione</h2>
          <p className="text-red-200">{error}</p>
          <button
            // Task #3260/#3585 — ritorno al MASTER: origin master dedicato condiviso.
            onClick={() => window.location.href = `${getMasterPublicBaseUrl()}/users`}
            className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Torna al Master Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
        <p className="text-white text-lg">Accesso in corso...</p>
        <p className="text-gray-400 text-sm mt-2">Impersonazione utente in corso</p>
      </div>
    </div>
  );
}
